import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';

const C = {
  bg: '#0D0D12', surface: '#16161F', card: '#1C1C28', border: '#2A2A3A',
  accent: '#E8A2B8', accentSoft: '#C97A9A', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5',
};

const DAY_ICONS = [
  { id: 'sex', emoji: '♥', label: 'Intimacy', color: '#E8A2B8' },
  { id: 'mood_low', emoji: '🌧', label: 'Low mood', color: '#7ECECA' },
  { id: 'cramps', emoji: '⚡', label: 'Cramps', color: '#FF6B7A' },
  { id: 'spotting', emoji: '●', label: 'Spotting', color: '#C97A9A' },
  { id: 'mood_high', emoji: '✦', label: 'High energy', color: '#D4AF7A' },
  { id: 'headache', emoji: '◎', label: 'Headache', color: '#8A8A9E' },
  { id: 'bloat', emoji: '◉', label: 'Bloating', color: '#7ECBA5' },
  { id: 'skinflare', emoji: '◈', label: 'Skin flare', color: '#FF9F7A' },
];

const cyclePhases = [
  { label: 'Menstrual', day: 'Days 1–5', color: '#E8A2B8', icon: '🌑' },
  { label: 'Follicular', day: 'Days 6–13', color: '#D4AF7A', icon: '🌒' },
  { label: 'Ovulation', day: 'Day 14', color: '#7ECECA', icon: '🌕' },
  { label: 'Luteal', day: 'Days 15–28', color: '#7ECBA5', icon: '🌖' },
];

function getPhaseColor(d) {
  if (d <= 5) return '#E8A2B8';
  if (d <= 13) return '#D4AF7A';
  if (d === 14) return '#7ECECA';
  return '#7ECBA5';
}

function getPhaseLabel(d) {
  if (d <= 5) return 'MENSTRUAL';
  if (d <= 13) return 'FOLLICULAR';
  if (d === 14) return 'OVULATION';
  return 'LUTEAL';
}

export default function Cycle({ user }) {
  const [selected, setSelected] = useState(14);
  const [logOpen, setLogOpen] = useState(false);
  const [dayData, setDayData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [periodSaving, setPeriodSaving] = useState(false);
  const [periodSaved, setPeriodSaved] = useState(false);
  const [periodDate, setPeriodDate] = useState(new Date().toISOString().split('T')[0]);

  const days = Array.from({ length: 28 }, (_, i) => i + 1);
  const token = localStorage.getItem('authToken');

  const handlePeriodStart = async () => {
    setPeriodSaving(true);
    try {
      await axios.post(`${API_URL}/api/cycles`, {
        startDate: periodDate,
        phase: 'menstrual',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPeriodSaved(true);
    } catch (err) {}
    setPeriodSaving(false);
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/logs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const map = {};
        r.data.forEach(log => {
          const day = new Date(log.date).getDate();
          if (!map[day]) map[day] = [];
          if (!map[day].includes(log.category)) map[day].push(log.category);
        });
        setDayData(map);
      }).catch(() => {});
  }, [saved]);

  const selectedIcons = dayData[selected] || [];

  const toggleIcon = async (iconId) => {
    const current = dayData[selected] || [];
    const isActive = current.includes(iconId);
    setSaving(true);
    try {
      if (!isActive) {
        const icon = DAY_ICONS.find(i => i.id === iconId);
        const date = new Date();
        date.setDate(selected);
        await axios.post(`${API_URL}/api/logs`, {
          date: date.toISOString().split('T')[0],
          category: iconId,
          value: 5,
          notes: icon?.label || iconId,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setDayData(prev => {
        const curr = prev[selected] || [];
        const updated = isActive ? curr.filter(x => x !== iconId) : [...curr, iconId];
        return { ...prev, [selected]: updated };
      });
    } catch (err) {}
    setSaving(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", color: C.text, fontSize: 22, margin: '0 0 4px' }}>Cycle Tracker</h2>
        <p style={{ fontSize: 12, color: C.muted }}>Tap a day to log how you feel</p>
      </div>

      {/* Period start */}
      <div style={{ background: C.card, borderRadius: 14, padding: '14px 16px', border: `1px solid ${C.accent}44` }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 10 }}>WHEN DID YOUR LAST PERIOD START?</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="date"
            value={periodDate}
            max={new Date().toISOString().split('T')[0]}
            onChange={e => { setPeriodDate(e.target.value); setPeriodSaved(false); }}
            style={{
              flex: 1, background: C.surface, border: `1.5px solid ${C.border}`,
              borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 13,
              outline: 'none', colorScheme: 'dark',
            }}
          />
          {periodSaved
            ? <div style={{ fontSize: 11, color: C.green, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>✓ LOGGED</div>
            : <button onClick={handlePeriodStart} disabled={periodSaving || !periodDate}
                style={{ background: C.accent, color: '#0D0D12', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {periodSaving ? '...' : 'Log start'}
              </button>
          }
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Day 1 = first day of bleeding</div>
      </div>

      {/* Icon legend */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {DAY_ICONS.map(ic => (
          <div key={ic.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: C.card, borderRadius: 20, padding: '3px 8px', border: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 10, color: ic.color }}>{ic.emoji}</span>
            <span style={{ fontSize: 9, color: C.muted }}>{ic.label}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ background: C.card, borderRadius: 18, padding: 14, border: `1px solid ${C.border}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 6 }}>
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: 9, color: C.muted, fontFamily: 'monospace' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
          {days.map(d => {
            const icons = dayData[d] || [];
            const isSelected = d === selected;
            const phaseColor = getPhaseColor(d);
            return (
              <div key={d} onClick={() => { setSelected(d); setLogOpen(true); }}
                style={{
                  borderRadius: 10,
                  background: isSelected ? `${phaseColor}22` : C.surface,
                  border: `1.5px solid ${isSelected ? phaseColor : C.border}`,
                  padding: '5px 2px 4px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                  cursor: 'pointer', minHeight: 48, transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: 10, color: isSelected ? C.text : C.muted, fontWeight: isSelected ? 700 : 400 }}>{d}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                  {icons.slice(0, 2).map(id => {
                    const ic = DAY_ICONS.find(i => i.id === id);
                    return ic ? <span key={id} style={{ fontSize: 8, color: ic.color, lineHeight: 1 }}>{ic.emoji}</span> : null;
                  })}
                  {icons.length > 2 && <span style={{ fontSize: 7, color: C.muted }}>+{icons.length - 2}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day log panel */}
      {logOpen && (
        <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${getPhaseColor(selected)}44` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <span style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>DAY {selected} · </span>
              <span style={{ fontSize: 11, color: getPhaseColor(selected), fontFamily: 'monospace' }}>{getPhaseLabel(selected)}</span>
            </div>
            <span onClick={() => setLogOpen(false)} style={{ fontSize: 14, color: C.muted, cursor: 'pointer' }}>✕</span>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>Tap to toggle</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
            {DAY_ICONS.map(ic => {
              const active = selectedIcons.includes(ic.id);
              return (
                <div key={ic.id} onClick={() => toggleIcon(ic.id)}
                  style={{
                    background: active ? `${ic.color}22` : C.surface,
                    border: `1.5px solid ${active ? ic.color : C.border}`,
                    borderRadius: 12, padding: '10px 6px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    cursor: 'pointer', transition: 'all 0.15s', opacity: saving ? 0.7 : 1,
                  }}>
                  <span style={{ fontSize: 18, color: ic.color }}>{ic.emoji}</span>
                  <span style={{ fontSize: 9, color: active ? ic.color : C.muted, textAlign: 'center', lineHeight: 1.2 }}>{ic.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase key */}
      <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 10 }}>PHASE KEY</div>
        {cyclePhases.map(p => (
          <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{p.icon}</div>
            <div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{p.label}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{p.day}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
